import React, { createContext, useContext, useState, useEffect } from 'react';
import { useBusiness } from '../shared/contexts/BusinessContext';

const SectorContext = createContext();

export const useSector = () => {
  const context = useContext(SectorContext);
  if (!context) {
    throw new Error('useSector must be used within a SectorProvider');
  }
  return context;
};

export const SectorProvider = ({ children }) => {
  const { selectedBusiness, businesses, isLoading: authLoading } = useBusiness();
  const [currentSector, setCurrentSector] = useState(null);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [sectorConfig, setSectorConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeSector = () => {
      // 🛡️ AUTH LOADING CHECK: Wait for AuthBusinessContext to finish loading initial state
      // We wait if auth is loading AND (either no business or business data is incomplete/stale)
      if (authLoading && (!selectedBusiness || !selectedBusiness.sector)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏳ SectorProvider: Auth still loading or business data incomplete, waiting...');
        }
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('💼 SectorProvider - Initializing with selectedBusiness:', selectedBusiness?.id || 'none');
      }
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Admin users get system management sector
      if (user.user_type === 'admin' || user.user_type === 'super_admin' || user.role === 'admin' || user.role === 'super_admin') {
        setCurrentSector('admin');
        setSectorConfig({
          name: 'Sistem Yönetimi',
          icon: 'cog',
          color: '#6C5CE7',
          modules: ['users', 'billing', 'analytics', 'support', 'settings']
        });
        setCurrentBusiness(null);
        setIsLoading(false);
      } else if (selectedBusiness) {
        // Use real business data from BusinessContext
        setCurrentBusiness(selectedBusiness);
        
        // Determine sector from business type or default to restaurant
        const businessSector = determineSectorFromBusiness(selectedBusiness);
        setCurrentSector(businessSector);
        
        // Set sector config based on business type
        const sectorConfig = getSectorConfig(businessSector, selectedBusiness);
        setSectorConfig(sectorConfig);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Sector initialized:', {
            sector: businessSector,
            business: selectedBusiness.name
          });
        }
        setIsLoading(false);
      } else {
        // 🛡️ Persist last known sector to avoid flicker if just refreshing
        const cachedSector = localStorage.getItem('currentSector');
        if (cachedSector && cachedSector !== 'admin') {
           setCurrentSector(cachedSector);
           // Config should ideally be reconstructed but we wait for re-auth
        }
        // Don't set default until we are SURE auth is done and failed to find a business
        if (!authLoading) {
          setCurrentSector('restaurant'); // Final fallback
          setSectorConfig({
            name: 'Restoran Yönetimi',
            icon: 'restaurant',
            color: '#3B82F6',
            modules: ['pos', 'kitchen', 'tables', 'menu', 'orders']
          });
          setCurrentBusiness(null);
          setIsLoading(false);
        }
      }
    };
    
    initializeSector();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, authLoading]);
  
  // 🎯 ROBUST SECTOR DETECTOR: Matches AuthBusinessContext more closely
  const determineSectorFromBusiness = (business) => {
    if (!business) return 'food-beverage';
    
    const type = (business.business_type || business.sector || business.sub_type || '').toLowerCase();
    const category = (business.category_name || business.category || '').toLowerCase();
    const bizName = (business.name || '').toLowerCase();
    
    // 🏨 Hospitality Detection (Strong check)
    const isHospitality = ['hotel', 'hostel', 'pansiyon', 'villa', 'apart', 'hospitality'].includes(type) ||
                         category.includes('otel') || 
                         category.includes('konaklama') || 
                         category.includes('hospitality') ||
                         bizName.includes('otel') ||
                         bizName.includes('hotel') ||
                         bizName.includes('pansiyon') ||
                         bizName.includes('konak');
    
    if (isHospitality) return 'service'; // Always 'service' sector for our architecture, sub-config handles hospitality

    // Map other types
    if (['restaurant', 'cafe', 'fast_food', 'bakery', 'pastry', 'food-beverage', 'food_beverage'].includes(type)) {
      return 'food-beverage';
    } else if (['retail', 'store', 'shop', 'market'].includes(type)) {
      return 'retail';
    } else if (['service', 'clinic', 'salon', 'repair', 'beauty', 'health', 'consulting', 'fitness'].includes(type)) {
      return 'service';
    }
    
    // Default fallback
    return 'food-beverage';
  };
  
  // Helper function to get sector configuration
  const getSectorConfig = (sector, business) => {
    const subType = (business?.sub_type || business?.business_type || business?.sector || '').toLowerCase();
    const categoryName = (business?.category_name || business?.category || '').toLowerCase();
    const bizName = (business?.name || '').toLowerCase();

    // 🏨 Hospitality Sub-Sector Detection
    const isHospitality = ['hotel', 'hostel', 'pansiyon', 'villa', 'apart', 'hospitality'].includes(subType) ||
                          categoryName.includes('otel') || 
                          categoryName.includes('konaklama') || 
                          categoryName.includes('hospitality') ||
                          bizName.includes('otel') ||
                          bizName.includes('hotel');

    const configs = {
      restaurant: {
        name: 'Restoran Yönetimi',
        icon: 'restaurant',
        color: '#10B981',
        modules: ['pos', 'kitchen', 'tables', 'menu', 'orders', 'inventory']
      },
      'food-beverage': {
        name: 'Restoran Yönetimi',
        icon: 'restaurant',
        color: '#10B981',
        modules: ['pos', 'kitchen', 'tables', 'menu', 'orders', 'inventory']
      },
      retail: {
        name: 'Perakende Yönetimi',
        icon: 'shopping',
        color: '#3B82F6',
        modules: ['inventory', 'pos', 'products', 'sales', 'customers']
      },
      service: isHospitality ? {
        name: 'Otelcilik Yönetimi',
        sub_sector: 'hospitality',
        hotel_type: subType,
        icon: 'hotel',
        color: '#1a1a2e',
        modules: ['rooms', 'reservations', 'hotel_finance', 'hotel_whatsapp', 'booking_engine']
      } : {
        name: 'Hizmet Yönetimi',
        icon: 'service',
        color: '#8B5CF6',
        modules: ['appointments', 'customers', 'billing', 'staff']
      }
    };

    return configs[sector] || configs['food-beverage'];
  };

  const switchSector = (sectorKey) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Switching to sector:', sectorKey);
    }
    setCurrentSector(sectorKey);
    
    // Update sector config
    const sectorConfig = getSectorConfig(sectorKey, currentBusiness);
    setSectorConfig(sectorConfig);
    
    localStorage.setItem('currentSector', sectorKey);
  };

  const switchBusiness = (businessId) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Switching to business:', businessId);
    }
    
    const business = businesses.find(b => b.id === businessId);
    if (business) {
      setCurrentBusiness(business);
      
      const businessSector = determineSectorFromBusiness(business);
      setCurrentSector(businessSector);
      
      const sectorConfig = getSectorConfig(businessSector, business);
      setSectorConfig(sectorConfig);
      
      localStorage.setItem('currentBusiness', businessId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Business switched:', {
          business: business.name,
          sector: businessSector
        });
      }
    }
  };

  const value = {
    currentSector,
    currentBusiness: currentBusiness || selectedBusiness,
    sectorConfig,
    isLoading,
    switchSector,
    switchBusiness,
    availableSectors: ['restaurant', 'retail', 'service'],
    // Helper functions
    determineSectorFromBusiness,
    getSectorConfig
  };

  return (
    <SectorContext.Provider value={value}>
      {children}
    </SectorContext.Provider>
  );
};
